// Sim headers, as the four contracts write them (docs/contracts/{hemo,dynamics,cells,pharma}-sim-v0.md).
// Only what the app reads is typed; unknown fields pass through untouched, as the contracts require.

import type { ArrayDescriptor } from './binary.ts';
import type { HeaderTime, NamedTimeBase } from './time.ts';

export type SimFormat = 'hemo' | 'dynamics' | 'cells' | 'pharma';

export const FORMATS: Record<string, SimFormat> = {
  'ba-hemo-sim-v0': 'hemo',
  'ba-dynamics-sim-v0': 'dynamics',
  'ba-cells-sim-v0': 'cells',
  'ba-pharma-sim-v0': 'pharma',
};

/** The asset-pack provenance labels; sims and their inputs use the same five. */
export type Provenance = 'measured' | 'derived' | 'reference' | 'simulated' | 'illustrative';

/** A timed event: hemo and pharma write `{type, t_s}`; dynamics `{t, label, kind}` (seconds). */
export interface SimEvent {
  type?: string;
  kind?: string;
  t_s?: number;
  time_s?: number;
  t?: number;
  segment?: string;
  label?: string;
  [k: string]: unknown;
}

export interface SimModel {
  name?: string;
  id?: string;
  version?: string;
  citation?: string;
  url?: string;
  equations?: string | string[] | Record<string, string>;
  metadata?: string;
  [k: string]: unknown;
}

/** One input. hemo/cells/pharma say `what`; dynamics says `role` and `name`. */
export interface SimInput {
  what?: string;
  role?: string;
  name?: string;
  provenance?: string;
  source?: string;
  method?: string;
  [k: string]: unknown;
}

/** The fields every contract shares (hemo-sim-v0's header, which the others follow). */
export interface CommonHeader {
  format: string;
  id: string;
  kind: string;
  scenario?: { name?: string; label?: string; events?: SimEvent[]; stimuli?: SimEvent[]; [k: string]: unknown };
  created?: string;
  space?: string;
  pack?: { id: string; version?: string } | null;
  provenance?: string;
  methods?: string;
  model?: SimModel;
  inputs?: SimInput[];
  units?: Record<string, string>;
  time?: HeaderTime;
  validation?: { summary?: string; report?: string | null; [k: string]: unknown };
  files?: Record<string, unknown>;
  pipeline?: { path?: string; commit?: string; sha256?: string };
  [k: string]: unknown;
}

// hemo-sim-v0

export interface HemoSegmentRaw {
  id: string;
  index?: number;
  name?: string;
  structure?: string | null;
  sn: number | string;
  tn: number | string;
  centreline?: { offset: number; count: number };
  length_mm?: number;
  radius_mm?: [number, number] | number;
  samples?: { offset: number; count: number; s: number[] };
  state?: 'open' | 'occluded' | 'absent';
  outlet?: { type?: string; territory?: string; [k: string]: unknown } | null;
  geometry?: { provenance?: string; method?: string };
  [k: string]: unknown;
}

export interface HemoGraphRaw {
  nodes: { id: number | string; xyz?: [number, number, number]; kind?: string }[];
  segments: HemoSegmentRaw[];
}

export interface HemoHeader extends CommonHeader {
  files?: {
    graph?: string;
    centrelines?: string;
    centreline_offsets?: string;
    centreline_radius?: string;
    series?: string[];
    volumes?: string[];
    surfaces?: string[];
    growth?: string;
    network?: string;
    [k: string]: unknown;
  };
}

// dynamics-sim-v0

export interface DynamicsParcel {
  index: number;
  source_id?: string;
  source_name?: string;
  structure?: string | null;
  dk_label?: number | null;
  dk_name?: string | null;
  relation?: string;
}

export interface DynamicsHeader extends CommonHeader {
  time_bases?: Record<string, NamedTimeBase & { units?: string }>;
  arrays?: Record<string, ArrayDescriptor>;
  parcels?: DynamicsParcel[];
  channels?: { eeg?: string[]; seeg?: string[]; [k: string]: string[] | undefined };
  display?: { activity_range?: [number, number]; time_scale?: number };
  /** The model's seizure onset, s on its visual and signal bases (a run fitted to a patient's seizure). */
  onset_s?: number;
  projection?: Record<string, unknown>;
  observation?: Record<string, unknown>;
}

// cells-sim-v0

export interface CellsSeriesInfo {
  quantity: string;
  dtype: string;
  decode?: { offset: number; scale: number };
  units?: string;
  range?: [number, number];
  frames_per_chunk: number;
  chunks: string[];
}

export interface CellsPlacement {
  /** The brain space the affine maps the run's `space` into. */
  space: string;
  /** 4×4 RAS mm, row-major. */
  affine: number[][];
  provenance?: string;
  method?: string;
  [k: string]: unknown;
}

export interface CellsHeader extends CommonHeader {
  counts?: { cells: number; vertices?: number; segments?: number; compartments: number; spikes?: number };
  series?: CellsSeriesInfo[];
  spikes?: { site?: string; threshold_mv?: number };
  placement?: CellsPlacement | null;
  files?: { cells?: string; events?: string | null; synapses?: string | null; lfp?: string | null; [k: string]: unknown };
  /** How the run was recorded: a close-up records some of the cells its simulation ran. */
  recording?: { simulated_cells?: number; [k: string]: unknown };
}

/** One `events.json` entry: a detected dendritic event, a bAP or a stimulus. Times in s. */
export interface CellEvent {
  kind: 'nmda-spike' | 'dendritic-na-spike' | 'ca-spike' | 'bap' | 'stimulus' | 'other' | (string & {});
  cell: number;
  compartments?: number[];
  t_on_s: number;
  t_off_s?: number;
  peak_mv?: number;
  peak_compartment?: number;
  peak_t_s?: number;
  extent_um?: number;
  reach_um?: number;
  /** Where, as the simulator names it: `dend[84](0.75)`. */
  site?: string;
  /** A stimulus's type, as in `scenario.stimuli`: `current-clamp`, `synaptic-cluster`. */
  stimulus?: string;
  [k: string]: unknown;
}

export interface CellEntry {
  index: number;
  id: string;
  name?: string;
  type?: string;
  class?: string;
  layer?: string;
  /** The model as the run names it ("Eyal 2018 active model cell0603_11_model_937 (…)"). */
  model?: string;
  /** Where its morphology comes from and how it was reconstructed. */
  morphology?: { provenance?: string; source?: string; method?: string; file?: string };
  /** Its soma centre in the run's `space`, mm. */
  soma_xyz?: [number, number, number];
  vertices?: { offset: number; count: number };
  segments?: { offset: number; count: number };
  compartments: { offset: number; count: number };
  recording?: 'full' | 'soma';
  [k: string]: unknown;
}

// pharma-sim-v0

export interface PharmaMapDescriptor extends ArrayDescriptor {
  quantity?: string;
  domain: 'parcel' | 'vertex-left' | 'vertex-right' | string;
  target_codes: string[];
  bound?: 'low' | 'central' | 'high' | string;
  time_axis?: number | null;
  mask?: (ArrayDescriptor & { static?: boolean }) | string | null;
}

export interface PharmaSeriesDescriptor extends ArrayDescriptor {
  quantity: string;
  channels: (string | { name?: string; compound?: string; target?: string; [k: string]: unknown })[];
  bound?: string;
  mask?: ArrayDescriptor | string | null;
}

export interface PharmaPrediction {
  target: string;
  affinity?: { low_nm?: number; central_nm?: number; high_nm?: number; n?: number; statistic?: string };
  occupancy?: { low?: number; central?: number; high?: number };
  [k: string]: unknown;
}

export interface PharmaHeader extends CommonHeader {
  name?: string;
  drug?: string;
  target_codes?: string[];
  /** The targets with cortex maps, where the header lists PK targets beyond them. */
  spatial_target_codes?: string[];
  primary_target?: string;
  structure_ids?: string[];
  predictions?: PharmaPrediction[];
  exposure_nm?: { low?: number; central?: number; high?: number };
  excluded?: { target: string; reason?: string }[];
  files?: { maps?: string[]; series?: string[]; surfaces?: string[]; particles?: string[]; events?: string[]; [k: string]: unknown };
}

/** `sims/<domain>/index.json` rows (all four contracts; pharma runs add their own fields). */
export interface IndexEntry {
  id: string;
  kind?: string;
  scenario?: string | { name?: string; label?: string; [k: string]: unknown };
  space?: string;
  path: string;
  created?: string;
  name?: string;
  /** Methods text for the run, written for the header whose SHA-256 is `header_sha256`. */
  methods?: string;
  header_sha256?: string;
  [k: string]: unknown;
}

/** What a run's index row says about it (its label, its Methods text), kept only for the header it was written for. */
export interface IndexAnnotation {
  label?: string;
  methods?: string;
}
